import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ImportProgress {
  folderId: string;
  folderPath: string;
  totalFiles: number;
  processedFiles: number;
  errors: string[];
  // Add scanning-specific fields
  directoriesScanned?: number;
  totalDirectories?: number;
  currentDirectory?: string;
  filesFound?: number;
}

export interface GlobalImportState {
  isImporting: boolean;
  activeImports: ImportProgress[];
  totalFilesAcrossAllImports: number;
  totalProcessedFilesAcrossAllImports: number;
  overallProgress: number; // 0-100
  currentPhase: 'scanning' | 'processing' | 'complete' | 'idle';
  importStartTime: number | null;
  estimatedTimeRemaining: number | null; // in seconds
  lastUpdated: number;
  canCancel: boolean;
  // Add scanning-specific state
  scanningProgress?: {
    directoriesScanned: number;
    totalDirectories: number;
    filesFound: number;
    currentDirectory: string;
  };
}

const initialState: GlobalImportState = {
  isImporting: false,
  activeImports: [],
  totalFilesAcrossAllImports: 0,
  totalProcessedFilesAcrossAllImports: 0,
  overallProgress: 0,
  currentPhase: 'idle',
  importStartTime: null,
  estimatedTimeRemaining: null,
  lastUpdated: 0,
  canCancel: false,
  scanningProgress: {
    directoriesScanned: 0,
    totalDirectories: 0,
    filesFound: 0,
    currentDirectory: '',
  },
};

const globalImportProgressSlice = createSlice({
  name: 'globalImportProgress',
  initialState,
  reducers: {
    startImport: (
      state,
      action: PayloadAction<{ folderId: string; folderPath: string }>,
    ) => {
      const { folderId, folderPath } = action.payload;

      // Check if this import already exists
      const existingImport = state.activeImports.find(
        (imp) => imp.folderId === folderId,
      );
      if (!existingImport) {
        state.activeImports.push({
          folderId,
          folderPath,
          totalFiles: 0,
          processedFiles: 0,
          errors: [],
        });
      }

      state.isImporting = true;
      state.currentPhase = 'scanning';
      state.canCancel = true;

      if (!state.importStartTime) {
        state.importStartTime = Date.now();
      }

      state.lastUpdated = Date.now();
    },

    updateImportProgress: (
      state,
      action: PayloadAction<{
        folderId: string;
        totalFiles?: number;
        processedFiles?: number;
        errors?: string[];
        phase?: 'scanning' | 'processing';
      }>,
    ) => {
      const { folderId, totalFiles, processedFiles, errors, phase } =
        action.payload;
      const importIndex = state.activeImports.findIndex(
        (imp) => imp.folderId === folderId,
      );

      if (importIndex !== -1) {
        const importProgress = state.activeImports[importIndex];

        if (totalFiles !== undefined) {
          importProgress.totalFiles = totalFiles;
        }

        if (processedFiles !== undefined) {
          importProgress.processedFiles = processedFiles;
        }

        if (errors) {
          importProgress.errors = [...importProgress.errors, ...errors];
        }

        if (phase) {
          state.currentPhase = phase;
        }
      }

      // Recalculate overall progress
      state.totalFilesAcrossAllImports = state.activeImports.reduce(
        (sum, imp) => sum + imp.totalFiles,
        0,
      );
      state.totalProcessedFilesAcrossAllImports = state.activeImports.reduce(
        (sum, imp) => sum + imp.processedFiles,
        0,
      );

      if (state.totalFilesAcrossAllImports > 0) {
        state.overallProgress = Math.round(
          (state.totalProcessedFilesAcrossAllImports /
            state.totalFilesAcrossAllImports) *
            100,
        );
      }

      // Calculate estimated time remaining
      if (state.importStartTime && state.overallProgress > 5) {
        const elapsed = Date.now() - state.importStartTime;
        const estimatedTotal = (elapsed / state.overallProgress) * 100;
        state.estimatedTimeRemaining = Math.round(
          (estimatedTotal - elapsed) / 1000,
        );
      }

      state.lastUpdated = Date.now();
    },

    completeImport: (state, action: PayloadAction<{ folderId: string }>) => {
      const { folderId } = action.payload;
      state.activeImports = state.activeImports.filter(
        (imp) => imp.folderId !== folderId,
      );

      // If no more active imports, reset state
      if (state.activeImports.length === 0) {
        state.isImporting = false;
        state.currentPhase = 'complete';
        state.overallProgress = 100;
        state.canCancel = false;
        state.estimatedTimeRemaining = 0;

        // Auto-reset to idle after 3 seconds
        setTimeout(() => {
          // Note: This won't work with Redux - we'd need a thunk or middleware
          // For now, we'll handle this in the component
        }, 3000);
      }

      state.lastUpdated = Date.now();
    },

    cancelImports: (state) => {
      state.activeImports = [];
      state.isImporting = false;
      state.currentPhase = 'idle';
      state.overallProgress = 0;
      state.totalFilesAcrossAllImports = 0;
      state.totalProcessedFilesAcrossAllImports = 0;
      state.importStartTime = null;
      state.estimatedTimeRemaining = null;
      state.canCancel = false;
      state.lastUpdated = Date.now();
      // Reset scanning progress
      if (state.scanningProgress) {
        state.scanningProgress.directoriesScanned = 0;
        state.scanningProgress.totalDirectories = 0;
        state.scanningProgress.filesFound = 0;
        state.scanningProgress.currentDirectory = '';
      }
    },

    resetToIdle: (state) => {
      return {
        ...initialState,
        scanningProgress: {
          directoriesScanned: 0,
          totalDirectories: 0,
          filesFound: 0,
          currentDirectory: '',
        },
      };
    },

    setScanComplete: (
      state,
      action: PayloadAction<{
        folderId: string;
        filesFound: number;
        errorsCount: number;
        errors?: string[];
      }>,
    ) => {
      const { folderId, filesFound, errorsCount, errors } = action.payload;
      const importIndex = state.activeImports.findIndex(
        (imp) => imp.folderId === folderId,
      );

      if (importIndex !== -1) {
        state.activeImports[importIndex].totalFiles = filesFound;
        if (errors) {
          state.activeImports[importIndex].errors = errors;
        }
        state.currentPhase = 'processing';
      }

      // Recalculate totals
      state.totalFilesAcrossAllImports = state.activeImports.reduce(
        (sum, imp) => sum + imp.totalFiles,
        0,
      );

      state.lastUpdated = Date.now();
    },

    updateScanningProgress: (
      state,
      action: PayloadAction<{
        directoriesScanned?: number;
        totalDirectories?: number;
        filesFound?: number;
        currentDirectory?: string;
      }>,
    ) => {
      const {
        directoriesScanned,
        totalDirectories,
        filesFound,
        currentDirectory,
      } = action.payload;

      if (state.scanningProgress) {
        if (directoriesScanned !== undefined) {
          state.scanningProgress.directoriesScanned = directoriesScanned;
        }
        if (totalDirectories !== undefined) {
          state.scanningProgress.totalDirectories = totalDirectories;
        }
        if (filesFound !== undefined) {
          state.scanningProgress.filesFound = filesFound;
        }
        if (currentDirectory !== undefined) {
          state.scanningProgress.currentDirectory = currentDirectory;
        }
      }

      state.lastUpdated = Date.now();
    },
  },
});

export const {
  startImport,
  updateImportProgress,
  completeImport,
  cancelImports,
  resetToIdle,
  setScanComplete,
  updateScanningProgress,
} = globalImportProgressSlice.actions;

export const selectGlobalImportProgress = (state: {
  globalImportProgress: GlobalImportState;
}) => state.globalImportProgress;

export const selectIsImporting = (state: {
  globalImportProgress: GlobalImportState;
}) => state.globalImportProgress.isImporting;

export const selectOverallProgress = (state: {
  globalImportProgress: GlobalImportState;
}) => state.globalImportProgress.overallProgress;

export const selectActiveImports = (state: {
  globalImportProgress: GlobalImportState;
}) => state.globalImportProgress.activeImports;

export const selectCurrentPhase = (state: {
  globalImportProgress: GlobalImportState;
}) => state.globalImportProgress.currentPhase;

export const selectEstimatedTimeRemaining = (state: {
  globalImportProgress: GlobalImportState;
}) => state.globalImportProgress.estimatedTimeRemaining;

export const selectScanningProgress = (state: {
  globalImportProgress: GlobalImportState;
}) => state.globalImportProgress.scanningProgress;

export default globalImportProgressSlice.reducer;
